/**
 * ProductGate — generic product-entitlement boundary for a route (REFUSAL-COPY-1 D3).
 *
 * D3's bug: `PRODUCT_NOT_ENTITLED` (403) reached the CPA as raw concatenated
 * text — `HttpErrorCatalog.format()` joining status/code/backend-message with
 * " | " — because nothing pre-empted the call. `/valuation` never has this
 * problem: `ValuationGate` (components/valuation/ValuationGate.tsx) checks
 * entitlement before the protected page ever renders, so the refusal is a
 * designed panel, not a caught exception. This generalizes that same shape so
 * every product-gated route gets it, not just Valuation.
 *
 * Where this differs from ValuationGate: Valuation's read roles ARE its
 * entitlement (a role IS a grant, VALUATOR-UI-1), so checking `user.role`
 * against a fixed set is sufficient. Other products separate the two layers —
 * a `cpa` role passes the route's role gate but the FIRM may still not hold
 * the product (RBAC-1 layer 1). This reads `user.products`, the same
 * three-state payload `lib/productNav.ts` already greys the nav from
 * (`entitled` / `accessible`), so this panel can never disagree with what the
 * sidebar already showed as greyed out.
 *
 * `products` undefined (the E2E claims-only bootstrap identity, built from a
 * decoded JWT before `/v1/me` has landed) is treated as accessible — the same
 * call `navItemsFor` makes and for the same reason: the server is the actual
 * gate, so a permissive gap here costs one 403 on click instead of blanking
 * the app for everyone whose `/me` call has not resolved yet.
 *
 * This is a UX gate, not a security boundary. The server is the authority:
 * every route is re-checked by `require_entitlement()` /
 * `require_product()` in apps/api/entitlements.py regardless of what this
 * component decides to render.
 *
 * `extraReasons` (REFUSAL-COPY-2, QOE-DEV-BATCH-2 WP-2): for a caller that
 * composes a SECOND lock on top of this one (ValuationGate — firm entitlement
 * here, then account role) and needs to say both when both fail, instead of
 * the CPA fixing one and coming back to find the other. Appended verbatim
 * below the entitlement reason, only while this panel itself is showing.
 * Optional and unused by every other caller — attest's `ProductGate` usage is
 * unaffected.
 *
 * PROVISION-GUARD-1: `user.products` is stamped once, at sign-in (`login()`)
 * or page load (`AuthProvider`'s mount effect) — never re-fetched again on
 * its own. A super admin grant landing mid-session therefore left the CPA
 * staring at this exact refusal panel with no way out except a full
 * re-login, even though the *server* was already correct — `/v1/me` re-derives
 * `entitled_products()` from the DB on every call, so the staleness is only
 * ever in this cached `user` object, never in what the backend enforces.
 * Chose (b) over a background poll: a silent timer that flips the screen
 * from under someone reads as a bug, and firm-level product grants change on
 * the order of "CPA calls Srikanth", not seconds — there is nothing to poll
 * for. `refreshUser()` re-fetches the SAME `/v1/me` this panel's `state`
 * already comes from, so a click either fixes it now or truthfully doesn't
 * (and this panel just keeps showing why).
 */
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import type { ProductKey } from '@/lib/productNav'

interface ProductGateProps {
  product: ProductKey
  /** Fallback label, used only when `user.products` has not landed yet and
   *  the payload's own `label` for this product is unavailable. */
  label: string
  /** Additional reason lines to show alongside the entitlement reason, for a
   *  caller composing a second lock on top of this one. See doc comment. */
  extraReasons?: string[]
  children: ReactNode
}

export function ProductGate({ product, label, extraReasons, children }: ProductGateProps) {
  const { user, refreshUser } = useAuth()
  const [checking, setChecking] = useState(false)
  const [checkedNoChange, setCheckedNoChange] = useState(false)
  const state = user?.products?.find(p => p.product === product)
  const accessible = state ? state.entitled && state.accessible : true

  if (accessible) return <>{children}</>

  const productLabel = state?.label ?? label
  const firm = user?.firm || 'Your firm'

  // PROVISION-GUARD-1: pulls a fresh /v1/me. If access was just granted this
  // re-renders straight past the panel into `children`; if not, `state` comes
  // back unchanged and the same reason renders again — never a silent flip.
  const checkAgain = async () => {
    setChecking(true)
    setCheckedNoChange(false)
    try {
      await refreshUser()
      setCheckedNoChange(true)
    } catch {
      // /v1/me failing here is the existing auth-refresh path's problem to
      // surface, not this panel's — leave the reason text as-is.
    } finally {
      setChecking(false)
    }
  }

  // Same two reasons lib/productNav.ts already distinguishes for the nav —
  // this is the same fact, worded as a sentence instead of a nav caption, and
  // deliberately mirrors the D1 backend copy (apps/api/entitlements.py
  // require_product()): name the firm, frame it as a purchase decision, not
  // a permissions one.
  const reason = !state?.entitled
    ? `${firm} does not have ${productLabel} on its plan. This is a ` +
      `purchase decision, not a permissions one — ask your firm ` +
      `administrator to add ${productLabel} to your plan.`
    : `${firm} has ${productLabel}, but this account does not have access ` +
      `to it. Ask your firm administrator for access.`

  return (
    <div className="mx-auto max-w-xl p-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h1 className="text-lg font-semibold text-gray-900">{productLabel}</h1>
        <p className="mt-2 text-sm text-gray-600">{reason}</p>
        {extraReasons?.map((extra, i) => (
          <p key={i} className="mt-2 text-sm text-gray-600">{extra}</p>
        ))}
        <dl className="mt-4 space-y-1 text-xs text-gray-500">
          <div className="flex gap-2">
            <dt>Signed in as</dt>
            <dd className="font-medium text-gray-700">{user?.email ?? '—'}</dd>
          </div>
          <div className="flex gap-2">
            <dt>Firm</dt>
            <dd className="font-mono text-gray-700">{firm}</dd>
          </div>
        </dl>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={checkAgain}
            disabled={checking}
            className="text-sm font-medium text-[#0d1f3c] underline disabled:opacity-50"
          >
            {checking ? 'Checking…' : 'Access was just granted — check again'}
          </button>
          <Link
            to="/dashboard"
            className="text-sm font-medium text-[#0d1f3c] underline"
          >
            Back to dashboard
          </Link>
        </div>
        {checkedNoChange && (
          <p className="mt-2 text-xs text-gray-500">
            Still no access as of just now. If your administrator says it was granted,
            try signing out and back in.
          </p>
        )}
      </div>
    </div>
  )
}
